import { useCallback, useState } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import { useMeldingen } from '../componenten/Meldingen.tsx';
import { pasVeldfoutenToe } from '../helpers/formulier.ts';

function veldnamen(waarden: unknown, voorvoegsel = ''): string[] {
  if (typeof waarden !== 'object' || waarden === null || Array.isArray(waarden)) {
    return [];
  }
  return Object.entries(waarden).flatMap(([naam, waarde]) => {
    const pad = `${voorvoegsel}${naam}`;
    return typeof waarde === 'object' && waarde !== null && !Array.isArray(waarde) && !(waarde instanceof Date)
      ? [pad, ...veldnamen(waarde, `${pad}.`)]
      : [pad];
  });
}

/**
 * Verzendt een formulier: valideert, roept de API aan, toont een bevestiging en koppelt fouten van de API aan
 * de velden. Fouten die bij geen enkel veld horen, komen terug als melding voor bovenaan het formulier.
 * @param formulier Het formulier van react-hook-form.
 * @param verzend Wat er met de gevalideerde waarden gebeurt.
 * @param opties De bevestiging na succes en wat er daarna gebeurt.
 * @returns De verzendfunctie voor het formulier, de algemene fout en of het verzenden bezig is.
 */
export function useVerzenden<T extends FieldValues>(
  formulier: UseFormReturn<T>,
  verzend: (waarden: T) => Promise<unknown>,
  opties: { melding?: string | undefined; naSucces?: (() => void) | undefined } = {},
) {
  const { meld } = useMeldingen();
  const [fout, setFout] = useState<string | null>(null);
  const [bezig, setBezig] = useState(false);

  const onVerzend = useCallback(
    () =>
      formulier.handleSubmit(async (waarden) => {
        setFout(null);
        setBezig(true);
        try {
          await verzend(waarden);
          if (opties.melding) {
            meld(opties.melding);
          }
          opties.naSucces?.();
        } catch (gevangen) {
          setFout(pasVeldfoutenToe(gevangen, formulier.setError, veldnamen(waarden)));
        } finally {
          setBezig(false);
        }
      })(),
    [formulier, verzend, opties.melding, opties.naSucces, meld],
  );

  const reset = useCallback(() => setFout(null), []);

  return { onVerzend, fout, bezig, reset };
}
